import { DepthBufferReader } from "@faro-lotv/lotv";
import {
  AlwaysDepth,
  DepthTexture,
  Matrix4,
  NoBlending,
  ShaderMaterial,
  Vector3,
  Vector4,
  WebGLRenderer,
  WebGLRenderTarget,
} from "three";
import { FullScreenQuad } from "three-stdlib";
import type { DepthSnapshot } from "./depth-surface";

const CAPTURE_SIZE = 256;

/**
 * @param point Clicked world point.
 * @param inverse Original camera's inverse view-projection.
 * @param width Source render width in physical pixels.
 * @param height Source render height in physical pixels.
 * @returns A native-resolution crop and its pixel-to-world transform.
 */
export function surfaceCaptureFrame(
  point: Vector3,
  inverse: Matrix4,
  width: number,
  height: number,
): { width: number; height: number; uv: Vector4; inverse: Matrix4 } {
  const projected = point.clone().applyMatrix4(inverse.clone().invert());
  const cropWidth = Math.min(CAPTURE_SIZE, width);
  const cropHeight = Math.min(CAPTURE_SIZE, height);
  const column = Math.max(
    0,
    Math.min(
      width - cropWidth,
      Math.floor(((projected.x + 1) * width) / 2 - cropWidth / 2),
    ),
  );
  const row = Math.max(
    0,
    Math.min(
      height - cropHeight,
      Math.floor(((projected.y + 1) * height) / 2 - cropHeight / 2),
    ),
  );
  const uv = new Vector4(
    column / width,
    row / height,
    cropWidth / width,
    cropHeight / height,
  );
  const frame = new Matrix4()
    .makeScale(uv.z, uv.w, 1)
    .setPosition(uv.x * 2 + uv.z - 1, uv.y * 2 + uv.w - 1, 0);
  return {
    width: cropWidth,
    height: cropHeight,
    uv,
    inverse: inverse.clone().multiply(frame),
  };
}

/** Owned, click-only depth crop; never changes the source camera or render target. */
export class SurfaceDepthCapture {
  private readonly depth = new DepthTexture(1, 1);
  private readonly target = new WebGLRenderTarget(1, 1, {
    depthTexture: this.depth,
  });
  private readonly material = new ShaderMaterial({
    blending: NoBlending,
    depthFunc: AlwaysDepth,
    vertexShader:
      "varying vec2 vUv; void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }",
    fragmentShader: `uniform sampler2D uDepth; uniform vec4 uCrop; varying vec2 vUv;
      void main() { gl_FragDepth = texture2D(uDepth, uCrop.xy + vUv * uCrop.zw).r;
      gl_FragColor = vec4(0.0); }`,
    uniforms: { uDepth: { value: null }, uCrop: { value: new Vector4() } },
  });
  private readonly quad = new FullScreenQuad(this.material);
  private readonly reader = new DepthBufferReader();

  /**
   * @param renderer Active renderer.
   * @param source Borrowed point-cloud depth target.
   * @param point Clicked world point.
   * @param inverse Original inverse view-projection.
   * @returns Independent native-detail depth and crop transform.
   */
  read(
    renderer: WebGLRenderer,
    source: WebGLRenderTarget,
    point: Vector3,
    inverse: Matrix4,
  ): DepthSnapshot {
    const frame = surfaceCaptureFrame(
      point,
      inverse,
      source.width,
      source.height,
    );
    const previous = renderer.getRenderTarget();
    const viewport = renderer.getViewport(new Vector4());
    const scissor = renderer.getScissor(new Vector4());
    const scissorTest = renderer.getScissorTest();
    const { autoClear } = renderer;
    try {
      this.target.setSize(frame.width, frame.height);
      this.material.uniforms.uDepth.value = source.depthTexture;
      this.material.uniforms.uCrop.value.copy(frame.uv);
      renderer.setRenderTarget(this.target);
      renderer.setViewport(0, 0, frame.width, frame.height);
      renderer.setScissorTest(false);
      renderer.autoClear = true;
      this.quad.render(renderer);
      this.reader.read(this.depth, frame.width, frame.height, renderer);
      return {
        width: frame.width,
        height: frame.height,
        depth: this.reader.depthBuffer().slice(),
        inverseViewProjection: frame.inverse,
        cameraInverseViewProjection: inverse.clone(),
      };
    } finally {
      renderer.setRenderTarget(previous);
      renderer.setViewport(viewport);
      renderer.setScissor(scissor);
      renderer.setScissorTest(scissorTest);
      renderer.autoClear = autoClear;
    }
  }

  /** Release only owned capture resources. */
  dispose(): void {
    this.reader.dispose();
    this.quad.dispose();
    this.material.dispose();
    this.target.dispose();
  }
}
